import {AppUrls} from 'shared/config/model/urls';
import {TARIFFS_ANCHOR_ID} from 'shared/lib/constants';

import React, {useCallback, useState} from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import ArrowForCheckbox from 'shared/ui/icons/ArrowForCheckbox';
import TelegramWithButton from 'shared/ui/icons/TelegramWithButton';
import Button from 'shared/ui/Button/Button/Button';
import {runtimeEnv} from 'shared/lib/runtimeEnv';

import styles from './FaqBlock.module.scss';

const FaqBlock: React.FC = () => {
    const [openItems, setOpenItems] = useState<number[]>([1]);
    const INFO_EMAIL = runtimeEnv('REACT_APP_INFO_EMAIL');
    const SUPPORT_TELEGRAM = runtimeEnv('REACT_APP_SUPPORT_TELEGRAM');

    const FAQ = [
        {
            id: 1,
            question: t`Храните ли вы данные банковских карт?`,
            // eslint-disable-next-line max-len
            answer: t`Нет. Данные карт не хранятся на стороне Overseer. Все операции, включая автоплатежи, обрабатываются через платёжный сервис Robokassa, который обеспечивает безопасное хранение и обработку платёжной информации.`,
        },
        {
            id: 2,
            question: t`Можно ли оплатить как юридическое лицо?`,
            // eslint-disable-next-line max-len
            answer: t`Да. В окне оплаты выберите вариант оплаты для юридических лиц и заполните форму. После этого на указанный e-mail будет отправлен счёт для безналичной оплаты.`,
        },
        {
            id: 3,
            question: t`Как работает продление текущего тарифа?`,
            // eslint-disable-next-line max-len
            answer: t`После оплаты новый период просто добавляется к оставшимся дням текущего тарифа. Итоговый срок действия увеличивается на оплаченный период без обнуления или перезапуска тарифа.`,
        },
        {
            id: 4,
            question: t`Можно ли перейти на другой тариф, пока текущий ещё активен?`,
            // eslint-disable-next-line max-len
            answer: t`Да, можно. Для переноса с учётом компенсации оставшихся дней свяжитесь с нами – мы проведём корректный перерасчёт вручную и добавим дни. Автоматический переход между тарифами находится в разработке.`,
        },
        {
            id: 5,
            question: t`Что произойдёт, если я не успею продлить тариф?`,
            // eslint-disable-next-line max-len
            answer: t`Мы отправляем напоминания за 3 дня, за 1 день, в день окончания и при истечении тарифа. Если продление не выполнено и автоплатёж отключён или не сработал, аккаунт автоматически переводится на Бесплатный тариф со всеми его ограничениями, а мониторы сверх лимита временно приостанавливаются.`,
        },
        {
            id: 6,
            question: t`Как работает автоплатёж?`,
            // eslint-disable-next-line max-len
            answer: t`В день окончания тарифа Robokassa автоматически выполняет списание и продлевает доступ, чтобы мониторинг не останавливался и инциденты не пропускались. Все операции проходят через Robokassa и полностью безопасны. Автоплатёж можно отключить в разделе «Платежи».`,
        },
        {
            id: 7,
            question: t`Осуществляете ли вы поддержку сайтов?`,
            // eslint-disable-next-line max-len
            answer: t`Да. Мы специализируемся на поддержке, доработке и развитии веб-проектов любой сложности – от быстрых правок до полноценного технического сопровождения. Свяжитесь с нами по e-mail ${INFO_EMAIL} или в Telegram ${SUPPORT_TELEGRAM}, и мы поможем решить вашу задачу.`,
        },
    ];

    const handleToggle = (id: number) => {
        setOpenItems(prev =>
            prev.includes(id)
                ? prev.filter(itemId => itemId !== id)
                : [...prev, id],
        );
    };

    const onPickTariffClick = useCallback(() => {
        document.getElementById(TARIFFS_ANCHOR_ID)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, []);

    const renderAnswer = (answer: string, id: number) => {
        if (id === 7) {
            return (
                <span className={styles.answer}>
                    Да. Мы специализируемся на поддержке, доработке и развитии веб-проектов любой сложности –
                    от быстрых правок до полноценного технического сопровождения. Свяжитесь с нами по e-mail{' '}
                    <a
                        href={AppUrls.INFO_EMAIL}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {runtimeEnv('REACT_APP_INFO_EMAIL')}
                    </a>{' '}
                    или в Telegram{' '}
                    <a
                        href={AppUrls.TELEGRAM}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                    >
                        @{runtimeEnv('REACT_APP_SUPPORT_TELEGRAM')}
                    </a>
                    , и мы поможем решить вашу задачу.
                </span>
            );
        }

        return <span className={styles.answer}>{answer}</span>;
    };

    return (
        <div className={styles.container}>
            <span className={styles.title}>Вопросы и ответы</span>
            <div className={styles.content}>
                {FAQ.map((item) => {
                    const isOpen = openItems.includes(item.id);

                    return (
                        <div
                            key={item.id}
                            className={cn(
                                styles.item,
                                isOpen && styles.itemOpen,
                            )}
                        >
                            <div className={styles.titleBlock} onClick={() => handleToggle(item.id)}>
                                <span className={styles.question}>{item.question}</span>
                                <div className={cn(
                                    styles.arrowWrapper,
                                    isOpen && styles.arrowOpen,
                                )}>
                                    <ArrowForCheckbox />
                                </div>
                            </div>
                            <div className={cn(
                                styles.answerWrapper,
                                isOpen && styles.answerVisible,
                            )}>
                                <span className={styles.answer}>{renderAnswer(item.answer, item.id)}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className={styles.buttons}>
                <Button
                    className={cn(styles.button, styles.tg)}
                    onClick={() => window.open(AppUrls.TELEGRAM, '_blank')}
                >
                    <TelegramWithButton />
                    Задать вопрос
                </Button>
                <Button className={cn(styles.button, styles.pickTariff)} onClick={onPickTariffClick}>
                    Выбрать тариф
                </Button>
            </div>
        </div>
    );
};

export default FaqBlock;